import UserProfile from "./UserProfile.js";

/**
 * @typedef {object} tetrisRawStat
 * @property {number} AvgScore
 * @property {number} HighScore
 * @property {number} NbrOfGames
 * @property {number} NbrOfTetris
 * @property {number} AvgTetrisPerGame
 * @property {number} NbrOfLinesCleared
 * @property {number} NbrOfTetriminosPlaced
 * @property {number} NbrOfTetrominosRotate
 * @property {number} AvgLinesClearedPerClear
 * @property {number} NbrOfClears - Helper field, not shown in the UI (mirrors backend UserStats.stats)
 */

const STORAGE_KEY = 'ft_transcendence_local_stats_v1'
const MAX_MATCH_HISTORY = 50
const DEFAULT_PROFILE_PICTURE = '/img/overlay/center/profile.jpg'

/**
 * Fields the stat view is allowed to read per category. Mirrors
 * accounts/utils.py STAT_CATEGORY_ALLOWED_FIELDS on the (now removed)
 * backend: the raw store below also keeps a couple of helper fields
 * (total_time_played, sum_avg_time_to_score_count, NbrOfClears, ...)
 * that only exist to make the running averages cheap to update, exactly
 * like UserStats.stats did server-side.
 */
const STAT_CATEGORY_ALLOWED_FIELDS = {
    pong: [
        'WinRate', 'NbrOfGames', 'NbrOfWins', 'NbrOfLosses',
        'AvgTimePerGame', 'NbrOfBounces', 'AvgTimeToScore',
    ],
    tetris: [
        'HighScore', 'AvgScore', 'NbrOfTetriminosPlaced', 'NbrOfTetris',
        'AvgTetrisPerGame', 'AvgLinesClearedPerClear', 'NbrOfLinesCleared',
        'NbrOfTetrominosRotate',
    ],
    pacman: [
        'HighScore', 'AvgScore', 'NbrOfGhostsEaten', 'AvgGhostsEatenPerGame',
        'NbrOfFruitsEaten', 'AvgFruitsEatenPerGame', 'NbrOfDeaths',
    ],
    AtariPong: [
        'NbrOfBounces', 'NbrOfPointsScored', 'NbrOfSoloGames', 'NbrOf1v1Games',
        'AvgTimePerSoloGame', 'AvgTimePer1v1Game', 'AvgTimeToScoreSolo', 'AvgTimeToScore1v1',
    ],
}

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100

function defaultTetrisStats() {
    return {
        AvgScore: 0,
        HighScore: 0,
        NbrOfGames: 0,
        NbrOfTetris: 0,
        AvgTetrisPerGame: 0,
        NbrOfLinesCleared: 0,
        NbrOfTetriminosPlaced: 0,
        NbrOfTetrominosRotate: 0,
        AvgLinesClearedPerClear: 0,
        NbrOfClears: 0,
    }
}

function defaultStats() {
    return {
        pong: {
            NbrOfGames: 0,
            NbrOfWins: 0,
            NbrOfLosses: 0,
            WinRate: 0,
            total_time_played: 0,
            NbrOfBounces: 0,
            AvgTimeToScore: 0,
            sum_avg_time_to_score_count: 0,
            AvgTimePerGame: 0,
        },
        tetris: defaultTetrisStats(),
        pacman: {
            NbrOfGames: 0,
            HighScore: 0,
            AvgScore: 0,
            NbrOfGhostsEaten: 0,
            AvgGhostsEatenPerGame: 0,
            NbrOfFruitsEaten: 0,
            AvgFruitsEatenPerGame: 0,
            NbrOfDeaths: 0,
        },
        AtariPong: {
            NbrOfBounces: 0,
            NbrOfPointsScored: 0,
            NbrOfSoloGames: 0,
            NbrOf1v1Games: 0,
            AvgTimePerSoloGame: 0,
            AvgTimePer1v1Game: 0,
            AvgTimeToScoreSolo: 0,
            AvgTimeToScore1v1: 0,
        },
        unified_match_history: [],
    }
}

/**
 * @author Sonam (scrumier) Crumiere
 * @class LocalStats
 * @description This is a public deployment: there is no account, no database and no
 * websocket pushing stats from the server any more. Every statistic the games compute
 * (each worker already posts a full `sendStats`/`result` payload) is folded in here and
 * persisted to localStorage instead of being POSTed to the backend. No statistic ever
 * touches the server or a database: clearing the site data resets the player's stats,
 * that is the intended behaviour.
 *
 * The aggregation math below is a line-for-line mirror of
 * backend/django/apps/games/views.py (GameResultView._update_single_player_stats,
 * _update_multiplayer_stats, _apply_tetris_match, _update_atari_time_metrics,
 * _build_match_entry) so the numbers a player sees are identical to what the removed
 * backend used to compute for the same sequence of games.
 */
class LocalStats {
    #stats

    constructor() {
        this.#stats = this.#load()
    }

    /**
     * @description Load the persisted stats from localStorage, filling in any
     * category missing from an older/partial payload with fresh defaults.
     * @returns {object}
     */
    #load() {
        const fallback = defaultStats()
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (!raw) {
                return fallback
            }
            const parsed = JSON.parse(raw)
            if (!parsed || typeof parsed !== 'object') {
                return fallback
            }
            return {
                pong: { ...fallback.pong, ...(parsed.pong || {}) },
                tetris: { ...fallback.tetris, ...(parsed.tetris || {}) },
                pacman: { ...fallback.pacman, ...(parsed.pacman || {}) },
                AtariPong: { ...fallback.AtariPong, ...(parsed.AtariPong || {}) },
                unified_match_history: Array.isArray(parsed.unified_match_history) ? parsed.unified_match_history : [],
            }
        } catch (error) {
            console.error('Unable to load local stats, starting fresh:', error)
            return fallback
        }
    }

    /**
     * @description Persist the current stats to localStorage and notify listeners
     * (statView / matchView) that they should refresh what they show.
     */
    #save() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#stats))
        } catch (error) {
            console.error('Unable to persist local stats:', error)
        }
        window.dispatchEvent(new CustomEvent('localStatsUpdated', {
            detail: {
                stats: this.getStats(),
                matchHistory: this.getMatchHistory(),
            }
        }))
    }

    /**
     * @description Return the subset of stats the stat view is allowed to read,
     * mirroring accounts/utils.py filter_user_stats_for_client.
     * @returns {object}
     */
    getStats() {
        const filtered = {}
        for (const [category, allowedFields] of Object.entries(STAT_CATEGORY_ALLOWED_FIELDS)) {
            const values = this.#stats[category] || {}
            filtered[category] = {}
            for (const field of allowedFields) {
                if (field in values) {
                    filtered[category][field] = values[field]
                }
            }
        }
        return filtered
    }

    /**
     * @description Return the local match history, most recent first.
     * @returns {object[]}
     */
    getMatchHistory() {
        return Array.isArray(this.#stats.unified_match_history) ? this.#stats.unified_match_history : []
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Mirrors GameResultView._update_single_player_stats' 'pacman' branch.
     * @param payload {{score:number, ghosts_eaten:number, fruits_eaten:number, deaths:number}}
     */
    recordPacmanMatch(payload = {}) {
        const score = payload.score || 0
        const ghostsEaten = payload.ghosts_eaten || 0
        const fruitsEaten = payload.fruits_eaten || 0
        const deaths = payload.deaths || 0

        const gameStats = this.#stats.pacman

        gameStats.NbrOfGames = (gameStats.NbrOfGames || 0) + 1
        gameStats.HighScore = Math.max(gameStats.HighScore || 0, score)
        gameStats.NbrOfGhostsEaten = (gameStats.NbrOfGhostsEaten || 0) + ghostsEaten
        gameStats.NbrOfFruitsEaten = (gameStats.NbrOfFruitsEaten || 0) + fruitsEaten
        gameStats.NbrOfDeaths = (gameStats.NbrOfDeaths || 0) + deaths

        if (gameStats.NbrOfGames > 1) {
            gameStats.AvgScore = Math.round((gameStats.AvgScore * (gameStats.NbrOfGames - 1) + score) / gameStats.NbrOfGames)
            gameStats.AvgFruitsEatenPerGame = round2(gameStats.NbrOfFruitsEaten / gameStats.NbrOfGames)
            gameStats.AvgGhostsEatenPerGame = round2(gameStats.NbrOfGhostsEaten / gameStats.NbrOfGames)
        } else {
            gameStats.AvgScore = round2(score)
            gameStats.AvgFruitsEatenPerGame = round2(fruitsEaten)
            gameStats.AvgGhostsEatenPerGame = round2(ghostsEaten)
        }

        this.#save()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Mirrors GameResultView._update_atari_time_metrics.
     */
    #updateAtariTimeMetrics(gameStats, countField, avgTimeField, avgScoreField, timePlayed, pointsScored) {
        const prevGames = gameStats[countField] || 0
        const prevAvgTime = gameStats[avgTimeField] || 0
        const prevAvgScore = gameStats[avgScoreField] || 0

        const newGames = prevGames + 1
        gameStats[countField] = newGames

        gameStats[avgTimeField] = round2((prevAvgTime * prevGames + timePlayed) / newGames)

        const timePerScore = pointsScored <= 0 ? timePlayed : timePlayed / pointsScored
        gameStats[avgScoreField] = round2((prevAvgScore * prevGames + timePerScore) / newGames)
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Mirrors GameResultView._update_single_player_stats' 'AtariPong' branch.
     * @param payload {{NbrOfBounces:number, NbrOfPointsScored:number, NbrOfSoloGames?:number, NbrOf1v1Games?:number, gameTime:number}}
     */
    recordAtariPongMatch(payload = {}) {
        const gameStats = this.#stats.AtariPong

        const bounces = payload.NbrOfBounces || 0
        const timePlayed = payload.gameTime || 0
        const pointsScored = payload.NbrOfPointsScored || 0
        const versusFlag = Number(payload.NbrOf1v1Games) || 0

        const isMultiplayer = versusFlag > 0

        gameStats.NbrOfBounces = (gameStats.NbrOfBounces || 0) + bounces
        gameStats.NbrOfPointsScored = (gameStats.NbrOfPointsScored || 0) + pointsScored

        if (isMultiplayer) {
            this.#updateAtariTimeMetrics(gameStats, 'NbrOf1v1Games', 'AvgTimePer1v1Game', 'AvgTimeToScore1v1', timePlayed, pointsScored)
        } else {
            this.#updateAtariTimeMetrics(gameStats, 'NbrOfSoloGames', 'AvgTimePerSoloGame', 'AvgTimeToScoreSolo', timePlayed, pointsScored)
        }

        this.#save()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Mirrors GameResultView._apply_tetris_match. Used for BOTH solo Tetris
     * and multiplayer Tetris: the backend folded 'multiplayerTetris' into the same 'tetris'
     * bucket as solo games, so this store does the same.
     * @param payload {{score:number, NbrOfTetriminosPlaced:number, NbrOfTetris:number, NbrOfClears:number, NbrOfLinesCleared:number, NbrOfTetrominosRotate:number}}
     */
    recordTetrisMatch(payload = {}) {
        const stats = this.#stats.tetris

        const score = payload.score || 0
        const linesCleared = payload.NbrOfLinesCleared || 0
        const tetrises = payload.NbrOfTetris || 0
        const tetrominosPlaced = payload.NbrOfTetriminosPlaced || 0
        const rotations = payload.NbrOfTetrominosRotate || 0
        const nbrOfClears = payload.NbrOfClears || 0

        const prevGames = stats.NbrOfGames || 0
        const newGames = prevGames + 1
        stats.NbrOfGames = newGames

        const prevClears = stats.NbrOfClears || 0
        const newClears = prevClears + nbrOfClears
        stats.NbrOfClears = newClears

        stats.HighScore = Math.max(stats.HighScore || 0, score)
        stats.NbrOfLinesCleared = (stats.NbrOfLinesCleared || 0) + linesCleared
        stats.NbrOfTetriminosPlaced = (stats.NbrOfTetriminosPlaced || 0) + tetrominosPlaced
        stats.NbrOfTetrominosRotate = (stats.NbrOfTetrominosRotate || 0) + rotations
        stats.NbrOfTetris = (stats.NbrOfTetris || 0) + tetrises

        const prevAvgScore = stats.AvgScore || 0
        stats.AvgScore = round2((prevAvgScore * prevGames + score) / newGames)

        const prevAvgTetris = stats.AvgTetrisPerGame || 0
        stats.AvgTetrisPerGame = round2((prevAvgTetris * prevGames + tetrises) / newGames)

        stats.AvgLinesClearedPerClear = newClears ? round2(stats.NbrOfLinesCleared / newClears) : 0

        this.#save()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Mirrors the 'pong' branch of GameResultView._update_multiplayer_stats.
     * Only used for online MacPong matches (there is no solo pong stat category).
     * The game server only ever tells the client the winner and both scores (see
     * gameServers/pongServer/Game/Game.py end_game): bounces, avgScoreTime and the
     * authoritative duration are computed server-side and never sent back down, so
     * they are passed here as 0 (matching the backend's own `.get(..., 0)` default)
     * instead of being guessed at client-side.
     * @param won {boolean}
     * @param timePlayed {number} - Best-effort client-side duration estimate (seconds)
     * @param bounces {number}
     * @param avgScoreTime {number}
     */
    #applyPongResult({ won, timePlayed = 0, bounces = 0, avgScoreTime = 0 }) {
        const gameStats = this.#stats.pong

        gameStats.NbrOfGames = (gameStats.NbrOfGames || 0) + 1
        if (won) {
            gameStats.NbrOfWins = (gameStats.NbrOfWins || 0) + 1
        } else {
            gameStats.NbrOfLosses = (gameStats.NbrOfLosses || 0) + 1
        }

        if (gameStats.NbrOfGames > 0) {
            gameStats.WinRate = round2((gameStats.NbrOfWins / gameStats.NbrOfGames) * 100)
        }

        gameStats.total_time_played = (gameStats.total_time_played || 0) + timePlayed
        gameStats.NbrOfBounces = (gameStats.NbrOfBounces || 0) + bounces

        if (avgScoreTime > 0) {
            const currentAvg = gameStats.AvgTimeToScore || 0
            const currentCount = gameStats.sum_avg_time_to_score_count || 0

            const newSum = currentAvg * currentCount + avgScoreTime
            const newCount = currentCount + 1
            gameStats.AvgTimeToScore = round2(newSum / newCount)
            gameStats.sum_avg_time_to_score_count = newCount
        }

        if (gameStats.NbrOfGames > 0) {
            gameStats.AvgTimePerGame = round2(gameStats.total_time_played / gameStats.NbrOfGames)
        }
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Build a match history entry, mirroring
     * GameResultView._build_match_entry.
     */
    #buildMatchEntry(gameType, { opponentName, opponentPicture, opponentUniqueId, userScore, opponentScore, durationSeconds, extra = {} }) {
        return {
            game_time: new Date().toISOString(),
            user_unique_id: UserProfile.getUniqueId(),
            opponent_unique_id: opponentUniqueId ?? null,
            opponent_name: opponentName || 'Guest Player',
            user_name: UserProfile.getDisplayName() || 'Guest',
            user_profile_picture: UserProfile.getProfilePicture() || DEFAULT_PROFILE_PICTURE,
            opponent_profile_picture: opponentPicture || DEFAULT_PROFILE_PICTURE,
            user_score: userScore,
            opponent_score: opponentScore,
            duration_seconds: durationSeconds || 0,
            game_type: gameType,
            ...extra,
        }
    }

    /**
     * @description Append a match history entry, keeping it sorted most-recent-first
     * and capped to 50 entries, mirroring _create_unified_match_entry.
     * @param entry {object}
     */
    addMatchHistoryEntry(entry) {
        const history = Array.isArray(this.#stats.unified_match_history) ? this.#stats.unified_match_history : []
        history.push(entry)
        history.sort((a, b) => new Date(b.game_time) - new Date(a.game_time))
        this.#stats.unified_match_history = history.slice(0, MAX_MATCH_HISTORY)
        this.#save()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Record a finished online MacPong match: updates the 'pong' aggregate
     * and appends a match history entry, exactly like a single POST to
     * /api/games/result/ used to do for game_type 'pong'.
     */
    recordOnlinePongMatch({ won, opponentName, opponentPicture, opponentUniqueId, userScore, opponentScore, durationSeconds, bounces = 0, avgScoreTime = 0 }) {
        this.#applyPongResult({ won, timePlayed: durationSeconds || 0, bounces, avgScoreTime })

        const entry = this.#buildMatchEntry('pong', {
            opponentName,
            opponentPicture,
            opponentUniqueId,
            userScore,
            opponentScore,
            durationSeconds,
            extra: {
                ball_bounces: bounces,
                avg_time_to_score: avgScoreTime,
            },
        })
        this.addMatchHistoryEntry(entry)
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Record a finished online (multiplayer) Tetris match: folds the
     * player's own richer stats (score/tetrominos/tetrises/lines/rotations, computed
     * client-side by the worker just like solo Tetris) into the same 'tetris' aggregate,
     * and appends a match history entry using win/loss as the score (matching
     * _build_match_entry's game_type == 'tetris' branch) plus the opponent's score and
     * lines cleared, the only opponent stats the game server relays to the client.
     */
    recordOnlineTetrisMatch({ won, myStats, opponentName, opponentPicture, opponentUniqueId, durationSeconds }) {
        this.recordTetrisMatch(myStats)

        const entry = this.#buildMatchEntry('tetris', {
            opponentName,
            opponentPicture,
            opponentUniqueId,
            userScore: won ? 1 : 0,
            opponentScore: won ? 0 : 1,
            durationSeconds,
            extra: {
                lines_cleared: (myStats && myStats.NbrOfLinesCleared) || 0,
                tetrises_cleared: (myStats && myStats.NbrOfTetris) || 0,
            },
        })
        this.addMatchHistoryEntry(entry)
    }

    /**
     * @description Wipe every locally stored statistic and match history entry.
     */
    reset() {
        this.#stats = defaultStats()
        this.#save()
    }
}

export default new LocalStats()
